import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Eye, Copy, Check } from "lucide-react";
import { generateTermsTemplate, type TermsTemplateData } from "@/lib/termsTemplate";
import { type TermsFieldsData } from "./TermsFieldsAccordion";
import { useToast } from "@/hooks/use-toast";

interface TermsPreviewButtonProps {
    termsFields: TermsFieldsData;
    referralBonusEntries: number;
    endsAt?: Date;
    startsAt?: Date;
    entryMethods?: string;
}

export function TermsPreviewButton({
    termsFields,
    referralBonusEntries,
    endsAt,
    startsAt,
    entryMethods = "Complete the actions listed on the contest page",
}: TermsPreviewButtonProps) {
    const [showPreview, setShowPreview] = useState(false);
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const formatDate = (date?: Date) => {
        if (!date) return "";
        return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    };

    const formatTime = (date?: Date) => {
        if (!date) return "";
        return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
    };

    const templateData: TermsTemplateData = {
        sponsorName: termsFields.sponsorName || "[Sponsor Name]",
        sponsorAddress: termsFields.sponsorAddress || "[Sponsor Address]",
        supportEmail: termsFields.supportEmail || "support@example.com",
        eligibleRegions: termsFields.eligibleRegions || "[Eligible Regions]",
        minimumAge: termsFields.minimumAge || "18",
        excludedStates: termsFields.excludedStates,
        startDate: startsAt ? formatDate(startsAt) : termsFields.startDate || "[Start Date]",
        startTime: startsAt ? formatTime(startsAt) : termsFields.startTime || "[Start Time]",
        endDate: endsAt ? formatDate(endsAt) : "[End Date]",
        endTime: endsAt ? formatTime(endsAt) : "[End Time]",
        entryMethods,
        mailInAddress: termsFields.mailInAddress,
        entryLimit: termsFields.entryLimit,
        prizeDescription: termsFields.prizeDescription || "[Prize Description]",
        prizeArv: termsFields.prizeArv || "[Prize ARV]",
        prizeQuantity: termsFields.prizeQuantity || "One (1)",
        totalPrizeValue: termsFields.totalPrizeValue,
        winnerNotificationMethod: termsFields.winnerNotificationMethod || "email",
        winnerNotificationTimeframe: termsFields.winnerNotificationTimeframe || "7 days",
        responseWindow: termsFields.responseWindow || "48 hours",
        winnerSelectionDate: termsFields.winnerSelectionDate,
        referralBonusEntries,
        governingState: termsFields.governingState || "[State]",
        includeMarketingOptIn: termsFields.includeMarketingOptIn === "yes",
    };

    const generatedTerms = generateTermsTemplate(templateData);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(generatedTerms);
            setCopied(true);
            toast({
                title: "Copied!",
                description: "Terms & Conditions copied to clipboard",
            });
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast({
                title: "Copy failed",
                description: "Please select and copy manually",
                variant: "destructive",
            });
        }
    };

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(true)}
                className="gap-2"
            >
                <Eye className="w-4 h-4" />
                Preview Terms
            </Button>

            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <DialogTitle>Generated Terms & Conditions Preview</DialogTitle>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCopy}
                                className="gap-2"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        Copy
                                    </>
                                )}
                            </Button>
                        </div>
                    </DialogHeader>
                    <ScrollArea className="h-[70vh] pr-4">
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground">
                            {generatedTerms}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </>
    );
}
